// src/components/Header.js
import React, {useState} from 'react';
import { AppBar, Toolbar, IconButton, Typography, Box, Button, Avatar, Menu, MenuItem, Divider, Tooltip } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import AuthModal from './AuthModal';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../store/authSlice';
import LogoutIcon from '@mui/icons-material/Logout';
import CreateDashboardModal from './CreateDashboardModal';
import RenameConversationModal from './RenameConversationModal';
import { fetchDashboards } from '../store/dashboardSlice';
import blackPlus from '../assets/black-plus.svg';
import barChart from '../assets/bar-chart.svg';
import StarBorder from './Buttons/StarBorder';
import IosShareIcon from '@mui/icons-material/IosShare';
import ShareDashboardModal from './ShareDashboardModal';
import ChatIcon from '../assets/chat-icon-black.svg';

const StyledAppBar = styled(AppBar)(({ theme, isDashboardPage, isAdvisorPage }) => ({
  backgroundColor: 'rgba(251, 251, 251, 1)', // Semi-transparent white
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)', // For Safari support
  color: theme.palette.header.text,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  borderBottom: '1px solid transparent', // Default transparent border
  
  // Conditional border for dashboard page
  ...(isDashboardPage && {
    borderBottom: `1px solid ${theme.palette.divider}`,
  }),

  // Add fade effect for advisor page - no blur, just gradient fade
  ...(isAdvisorPage && {
    backgroundColor: 'transparent', // Remove any solid background
    backdropFilter: 'none', // Remove blur effect
    WebkitBackdropFilter: 'none', // Remove blur effect for Safari
    backgroundImage: 'linear-gradient(to bottom, rgba(255, 255, 255, 1) 40%, rgba(255, 255, 255, 0) 100%)',
    borderBottom: 'none',
  }),
  position: 'fixed',
  zIndex: 1100,
}));

const SignInButton = styled(Button)(() => ({
  backgroundColor: '#129de8',
  color: '#fff',
  borderRadius: '20px',
  textTransform: 'none',
  fontSize: '16px',
  marginLeft: '8px',
  width: '80px',
  '&:hover': {
    backgroundColor: '#0c8ecf',
  },
}));


const ProfileMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: '8px 24px',
  margin: '0 8px 8px 8px',
  borderRadius: '4px',
  width: 'calc(100% - 16px)',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const UserInfoContainer = styled(Box)(({ theme }) => ({
  padding: '16px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}));

function Header({ isSidebarOpen, width, currentQuery, hasPrevMessage, hasNextMessage, onPrevArrowClick, onNextArrowClick, viewMode, isFetchingMessages, onRefreshClick, onCloseOutputView, backToGridMode = false, onViewFinalDashboard, showViewFinalDashboard = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  // const { conversationId } = useParams();
  const theme = useTheme();
  const dispatch = useDispatch();

  // Access auth state from Redux
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { dashboardList } = useSelector((state) => state.dashboard);
  const { activeConversation } = useSelector((state) => state.conversation);

  // State to control the AuthModal open/close
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [isSignInMode, setIsSignInMode] = useState(true);

  // Add these new states for the profile menu
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  // New state for dashboard modal
  const [createDashboardModalOpen, setCreateDashboardModalOpen] = useState(false);
  
  // New state for rename conversation modal
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  
  // New state for edit dashboard modal
  const [editDashboardModalOpen, setEditDashboardModalOpen] = useState(false);
  const [dashboardToEdit, setDashboardToEdit] = useState(null);
  
  // Share dashboard modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);

  /*
  useEffect(() => {
    if (!isAuthenticated) {
      setAuthModalOpen(true);
    }
  }, [isAuthenticated]);
  */
  
  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  // Helper function to get initials
  const getInitials = (firstName, lastName) => {
    if (!firstName || !lastName) return '??';
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  };

  const handleOpenAuthModal = () => {
    setAuthModalOpen(true);
  };

  const handleCloseAuthModal = () => {
    setAuthModalOpen(false);
  };

  // Sign out
  const handleSignOut = () => {
    dispatch(logout());
    localStorage.removeItem('jwtToken'); // remove token
    setIsSignInMode(true); // Reset to sign-in mode
    navigate('/');
  };

  // Handle mode toggle from AuthModal
  const handleAuthModeToggle = (isSignIn) => {
    setIsSignInMode(isSignIn);
  };

  // Add dashboard handler
  const handleAddDashboard = () => {
    setCreateDashboardModalOpen(true);
  };

  // Handle conversation title click
  const handleConversationTitleClick = () => {
    if (activeConversation) {
      setRenameModalOpen(true);
    }
  };

  // Handle dashboard title click
  const handleDashboardTitleClick = () => {
    const currentDashboard = dashboardList.find(d => d.dashboard_id === currentDashboardId);
    if (currentDashboard) {
      setDashboardToEdit(currentDashboard);
      setEditDashboardModalOpen(true);
    }
  };

  // Determine if we're on the Dashboards page to render a different header
  const isDashboardsPage = location.pathname === '/my-dashboards';
  const isHomePage = location.pathname === '' || location.pathname === '/';
  const isConversationPage = location.pathname.startsWith('/conversation');
  const isAcceptSharePage = location.pathname.startsWith('/dashboard/share/accept');
  const isSpecificDashboardPage = location.pathname.startsWith('/dashboard/') && !isAcceptSharePage;
  const currentDashboardId = isSpecificDashboardPage ? location.pathname.split('/').pop() : null;

  return (
    <>
      <StyledAppBar 
        position="fixed" 
        elevation={0} 
        isDashboardPage={isSpecificDashboardPage}
        isHomePage={isHomePage}
        sx={{
          width: `calc(100% - ${width}px)`,
          marginLeft: `${width}px`,
        }}
      >
        <Toolbar
          sx={{
            height: isHomePage ? 50 : 45,
            minHeight: isHomePage ? 50 : 45,
            // Override MUI default toolbar min-heights (56 on xs, 64 on sm+)
            '@media (min-width: 0px)': { minHeight: isHomePage ? 50 : 45, paddingLeft: isHomePage ? '20px' : '10px', paddingRight: '10px' },
            '@media (min-width: 600px)': { minHeight: isHomePage ? 50 : 45, paddingLeft: isHomePage ? '20px' : '10px', paddingRight: '10px' },
          }}
        >
          {isDashboardsPage && (
            <Typography variant="h4" noWrap component="div" sx={{ flexGrow: 1 }}>
              My Dashboards
            </Typography>
          )}
          {isHomePage && (
            <Typography variant="h4" noWrap component="div" sx={{ flexGrow: 1, fontSize: '24px' }}>
              Beta
            </Typography>
          )}
          {isConversationPage && (
            <>
              {/* Left side - Query text or conversation title - only show when not fetching messages */}
              {/* !isFetchingMessages && ( */}
              { activeConversation && (
              <Tooltip title="Rename Conversation" arrow={false} slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -10],
                        },
                      },
                    ],
                  },
                }} 
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: 'rgba(255, 255, 255, 0.65)',
                      color: '#000',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      fontSize: '12px',
                      border: '1px solid rgba(200, 200, 200, 0.6)',
                      borderRadius: '10px',
                      padding: '8px 12px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    },
                  },
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  minWidth: 0, maxWidth: '50%', 
                  backgroundColor: 'none', 
                  cursor: 'pointer', 
                  padding: '8px 0px', 
                  border: '1px solid transparent',
                  borderRadius: '8px', 
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    padding: '8px 8px', 
                    border: '1px solid rgba(204, 204, 204, 0.4)',
                    backgroundColor: '#ffffff',
                    boxShadow: 'rgba(0, 0, 0, 0.06) 0px 2px 6px',
                  },
                }} onClick={handleConversationTitleClick}>
                  <Box component="img" src={ChatIcon} alt="Chat Icon" sx={{ width: 20, height: 20, mr: '8px' }} />
                  <Typography 
                      variant="h4" 
                      noWrap 
                      component="div"
                      sx={{ 
                        fontSize: '16px',
                      }}
                    >
                      {activeConversation?.title || ''}
                    </Typography>
                </Box>
              </Tooltip>
              )}
              {/* Utility buttons - only show when not fetching messages */}
              <Box sx={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 0,
              }}>
                {!isFetchingMessages && (
                  <>
                    {/* Navigation + actions for multiagent output view */}
                    {(viewMode === 'output' || viewMode === 'dashboard') && (
                      <>
                        {/* Previous Message Button
                        {hasPrevMessage && (
                          <Tooltip title="Previous Message" arrow={false} componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: 'rgba(255, 255, 255, 0.65)',
                                color: '#000',
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)',
                                fontSize: '14px',
                                border: '1px solid rgba(200, 200, 200, 0.6)',
                                borderRadius: '10px',
                                padding: '8px 12px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                              },
                            },
                          }}>
                            <IconButton
                              onClick={onPrevArrowClick}
                              sx={{
                                color: 'rgba(0, 0, 0, 0.6)',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {hasNextMessage && (
                          <Tooltip title="Next Message" arrow={false} componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: 'rgba(255, 255, 255, 0.65)',
                                color: '#000',
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)',
                                fontSize: '14px',
                                border: '1px solid rgba(200, 200, 200, 0.6)',
                                borderRadius: '10px',
                                padding: '8px 12px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                              },
                            },
                          }}>
                            <IconButton
                              onClick={onNextArrowClick}
                              sx={{
                                color: 'rgba(0, 0, 0, 0.6)',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Refresh current query 
                        <Tooltip title="Retry" arrow={false} componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: 'rgba(255, 255, 255, 0.65)',
                              color: '#000',
                              backdropFilter: 'blur(10px)',
                              WebkitBackdropFilter: 'blur(10px)',
                              fontSize: '14px',
                              border: '1px solid rgba(200, 200, 200, 0.6)',
                              borderRadius: '10px',
                              padding: '8px 12px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                            },
                          },
                        }}>
                          <IconButton
                            onClick={onRefreshClick}
                            sx={{
                              color: 'rgba(0, 0, 0, 0.6)',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              },
                            }}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip> */}

                        {/* Tabs in DashboardView replaced the "View All Visuals" / "View Final Dashboard" header buttons */}

                        {/* Close multiagent output view
                        {typeof onCloseOutputView === 'function' && !backToGridMode && (
                          <Tooltip title="Close Multiagent View" arrow={false} componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: 'rgba(255, 255, 255, 0.65)',
                                color: '#000',
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)',
                                fontSize: '14px',
                                border: '1px solid rgba(200, 200, 200, 0.6)',
                                borderRadius: '10px',
                                padding: '8px 12px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                              },
                            },
                          }}>
                            <IconButton
                              onClick={onCloseOutputView}
                              sx={{
                                color: 'rgba(0, 0, 0, 0.6)',
                                borderRadius: '20px',
                                padding: '6px 6px',
                                ml: 0,
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                              size="small"
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* Divider
                        {viewMode !== 'streaming' && currentQuery && (
                          <Divider orientation="vertical" flexItem sx={{ 
                            height: 24, 
                            mx: 1,
                            mt: 'auto',
                            mb: 'auto',
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                          }} />
                        )} */}
                      </>
                    )}
                  </>
                )}
              </Box>
              
              {/* Right side spacer */}
              <Box sx={{ minWidth: 0, maxWidth: '50%' }} />
            </>
          )}

          {isSpecificDashboardPage && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexGrow: 1,
              width: '100%',
            }}>
              {/* Dashboard Title */}
              <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
                <Typography 
                  variant="h6" 
                  noWrap
                  onClick={handleDashboardTitleClick}
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '1.25rem',
                    color: theme.palette.text.primary,
                    cursor: 'pointer',
                    transition: 'text-decoration 0.2s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {dashboardList.find(d => d.dashboard_id === currentDashboardId)?.title || 'Dashboard'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                {/* New Dashboard Button */}
                <Button
                  variant="outlined"
                  startIcon={<Box component="img" src={blackPlus} alt="Plus icon" sx={{ width: 20, height: 20 }} />}
                  onClick={handleAddDashboard}
                  sx={{ 
                    borderRadius: '10px',
                    textTransform: 'none',
                    border: '1px solid #F4F4F4',
                    backgroundColor: '#F4F4F4',
                    color: 'black',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      border: '1px solid #e8e8e8',
                      backgroundColor: '#E8E8E8',
                    },
                  }}
                >
                  New Dashboard
                </Button>

                {/* Refresh Visuals Button
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    if (currentDashboardId) {
                      dispatch(refreshVisualsForDashboard(currentDashboardId));
                    }
                  }}
                  sx={{ 
                    borderRadius: '10px',
                    textTransform: 'none',
                    border: '1px solid #F4F4F4',
                    backgroundColor: '#F4F4F4',
                    color: 'black',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      border: '1px solid #e8e8e8',
                      backgroundColor: '#E8E8E8',
                    },
                  }}
                >
                  Refresh Dashboard
                </Button> */}

                {/* Share Dashboard Button */}
                <Button
                  variant="outlined"
                  startIcon={<IosShareIcon sx={{ mb: '2px', mr: '-2px' }} />}
                  onClick={() => setShareModalOpen(true)}
                  sx={{ 
                    borderRadius: '10px',
                    textTransform: 'none',
                    borderColor: '#129de8',
                    backgroundColor: '#129de8',
                    color: 'white',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderColor: '#108DD1',
                      backgroundColor: '#108DD1',
                    },
                  }}
                >
                  Share Dashboard
                </Button>

                {/* Create New Visual Button */}
                <Button
                  variant="outlined"
                  startIcon={<Box component="img" src={barChart} alt="Bar chart icon" sx={{ width: 20, height: 20 }} />}
                  onClick={() => navigate('/')}
                  sx={{ 
                    borderRadius: '10px',
                    textTransform: 'none',
                    borderColor: '#129de8',
                    backgroundColor: '#129de8',
                    color: 'white',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderColor: '#108DD1',
                      backgroundColor: '#108DD1',
                    },
                    mr: '12px',
                  }}
                >
                  Create New Visual
                </Button>
              </Box>
            </Box>
          )}
          
          {!isConversationPage && <Box sx={{ flexGrow: isSpecificDashboardPage ? 0 : 1 }} />}

          <IconButton 
            color="inherit" 
            onClick={() => window.open('https://vesterai.com', '_blank')}
            sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }, mr: isHomePage ? '2.5px' : '-5px' }}
          >
            <HomeIcon />
          </IconButton>

          {isHomePage && (
            <StarBorder
              as="button"
              className=""
              color="yellow"
              speed="2.5s"
              thickness={3}
              onClick={() => window.open('https://buy.stripe.com/8x214pa8Afc62fK50F2Fa04', '_blank')}
            >
              Subscribe Early
            </StarBorder>
          )}

          {isAuthenticated ? (
            <>
              <IconButton
                onClick={handleProfileClick}
                size="small"
                sx={{ ml: '5px'}}
                aria-controls={open ? 'profile-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
              >
                <Avatar 
                  sx={{ 
                    width: isHomePage ? 40 : 34, 
                    height: isHomePage ? 40 : 34, 
                    bgcolor: '#129de8',
                    fontSize: isHomePage ? '14px' : '12px',
                    fontWeight: 500
                  }}
                >
                  {getInitials(user?.user?.firstName, user?.user?.lastName)}
                </Avatar>
              </IconButton>
              <Menu
                id="profile-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleProfileClose}
                onClick={handleProfileClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.2))',
                    borderRadius: '20px',
                    background: 'rgb(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    border: '1px solid rgba(200, 200, 200, 0.3)',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
                    mt: 1.5,
                    minWidth: 220,
                    maxWidth: 400,
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <UserInfoContainer>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '18px' }}>
                    {`${user?.user?.firstName} ${user?.user?.lastName}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '16px' }}>
                    {user?.user?.email}
                  </Typography>
                </UserInfoContainer>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', my: 1 }}>
                  <Divider sx={{ width: '90%', borderWidth: 1, borderColor: theme.palette.divider }} />
                </Box>
                
                <ProfileMenuItem onClick={handleSignOut} sx={{ padding: '8px 16px', '&:hover': { borderRadius: '12px' } }}>
                  <LogoutIcon sx={{ mr: '10px' }} />
                  Sign Out
                </ProfileMenuItem>
              </Menu>
            </>
          ) : (
            <SignInButton onClick={handleOpenAuthModal}>
              {isSignInMode ? 'Sign In' : 'Sign Up'}
            </SignInButton>
          )}

        </Toolbar>
      </StyledAppBar>

      {/* Render the AuthModal */}
      <AuthModal 
        open={authModalOpen} 
        onClose={handleCloseAuthModal} 
        showSignIn={isSignInMode}
        onToggleMode={handleAuthModeToggle}
      />
      
      {/* Create Dashboard Modal */}
      <CreateDashboardModal
        open={createDashboardModalOpen}
        onClose={() => setCreateDashboardModalOpen(false)}
        onSuccess={(dashboardId) => {
          // Refresh dashboard list
          dispatch(fetchDashboards(user?.user?.id));
          
          // If a dashboard ID is provided, navigate to that dashboard
          if (dashboardId) {
            navigate(`/dashboard/${dashboardId}`);
          }
        }}
      />
      
      {/* Edit Dashboard Modal */}
      <CreateDashboardModal
        open={editDashboardModalOpen}
        onClose={() => {
          setEditDashboardModalOpen(false);
          setDashboardToEdit(null);
        }}
        dashboardToEdit={dashboardToEdit}
        onSuccess={() => {
          // Refresh dashboard list
          dispatch(fetchDashboards(user?.user?.id));
        }}
      />
      
      {/* Rename Conversation Modal */}
      <RenameConversationModal
        open={renameModalOpen}
        onClose={() => setRenameModalOpen(false)}
        conversation={activeConversation}
      />

      {/* Share Dashboard Modal */}
      <ShareDashboardModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        dashboardId={currentDashboardId}
      />
    </>
  );
}

export default React.memo(Header);
